import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Base } from 'src/containers';
import { Message } from 'src/components';
import { ROUTES } from 'src/config';
import CONSTANTS from 'src/store/constants';
import useSWR from 'swr';
import get from 'lodash.get';
import { fetchSegmentsExperimental, fetchModels } from 'src/store/api';
import { checkHiddenByRole } from 'src/functions';

function Home({ role, siteRole }) {
  const loggedInBefore = +localStorage.getItem('loggedInBefore');
  if (!loggedInBefore) localStorage.setItem('loggedInBefore', '1');

  const { data: segments, isLoading: isLoadingSegments } = useSWR('init_seg', () =>
    fetchSegmentsExperimental(
      {
        filters: [
          { name: 'is_hidden', op: 'eq', val: false },
          {
            name: 'filter',
            op: 'not_in',
            val: [
              'CLUSTERING_V3/2',
              'CLUSTERING_V3/3',
              'CLUSTERING_V3/4',
              'CLUSTERING_V3/5',
              'CLUSTERING_V3/6',
              'CLUSTERING_V3/2/SELECTED',
              'CLUSTERING_V3/3/SELECTED',
              'CLUSTERING_V3/4/SELECTED',
              'CLUSTERING_V3/5/SELECTED',
              'CLUSTERING_V3/6/SELECTED',
            ],
          },
        ],
      },
      { headers: { 'X-Fields': 'items{id,dataset{hiddenByRole}},*' } }
    )
  );

  const hiddenFor = useMemo(() => {
    const hiddenIdx = CONSTANTS.ROLES_HIDDEN.findIndex((name) => name === role);
    if (hiddenIdx === -1) return [];
    return CONSTANTS.ROLES_HIDDEN.slice(hiddenIdx);
  }, [role]);

  const { data: models, isLoading: isLoadingModels } = useSWR('init_models', () =>
    fetchModels(
      {
        filters: [
          {
            name: 'recent_status',
            op: 'eq',
            val: CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS,
          },
          {
            name: 'hidden_by_role',
            op: 'not_in',
            val: hiddenFor,
          },
          { name: 'data_model_type', op: 'neq', val: CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL },
        ],
      },
      { headers: { 'X-Fields': 'items{modelData{*}},*' } }
    )
  );

  const hasModels = useMemo(() => {
    if (models) {
      return Boolean(models.filter((model) => !get(model, 'modelData.metadataPayload.shared', false)).length);
    }
    return null;
  }, [models]);

  const hasSegments = useMemo(() => {
    if (segments) {
      return Boolean(
        segments.filter((segment) => !checkHiddenByRole(siteRole || role, get(segment, 'dataset.hiddenByRole'))).length
      );
    }
    return null;
  }, [role, segments, siteRole]);

  if (
    isLoadingModels ||
    isLoadingSegments ||
    [hasSegments, hasModels].includes(null) ||
    ['', null, undefined].includes(role)
  )
    return (
      // NOTE: Loader when in-flight requests and still waiting on check for models or segments
      <Base>
        <Message />
      </Base>
    );
  if (!loggedInBefore || (!hasModels && !hasSegments))
    return (
      // NOTE: First login or when no models and no segments, go to onb landing
      <Base>
        <Redirect to={{ pathname: ROUTES.ONBOARDING_LANDING }} />
      </Base>
    );
  if (hasModels)
    return (
      <Base>
        <Redirect to={{ pathname: ROUTES.MODELS }} />
      </Base>
    );

  return (
    <Base>
      <Redirect to={{ pathname: ROUTES.SEGMENTS }} />
    </Base>
  );
}

Home.propTypes = {
  role: PropTypes.string.isRequired,
  siteRole: PropTypes.string.isRequired,
};

const mapStateToProps = ({ app }) => ({
  role: app?.profile?.role,
  siteRole: app?.profile?.siteRole,
});

const mapDispatchToProps = () => {};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
